import React, { useState } from 'react';
import {
  MagnifyingGlassIcon,
  TrashIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XMarkIcon,
  ArchiveBoxIcon,
  ArchiveBoxXMarkIcon,
  ServerStackIcon,
  ShieldCheckIcon,
  ShieldExclamationIcon,
} from '@heroicons/react/24/outline';
import { scanStale, archiveTenant, purgeTenant } from '../services/tenantCleanupService';

/**
 * Stale Tenant Cleanup Panel
 *
 * Administrative interface for scanning, archiving, and purging stale tenants.
 * Part of UC-018: Stale Tenant Cleanup.
 *
 * Safety model (§8):
 *  - Two-phase: a tenant must be ARCHIVED before it can be PURGED.
 *  - Mandatory dry-run: the purge modal forces a dry-run preview first.
 *  - Exact-match token: the destructive confirm button is disabled until the
 *    admin types the exact ORG_ID (confirm_token === org_id).
 *  - Guardrail result + confirmation number surfaced from the backend.
 */
const StaleTenantCleanupPanel = () => {
  // Scan state
  const [candidates, setCandidates] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [hasScanned, setHasScanned] = useState(false);

  // Per-row action state
  const [rowBusy, setRowBusy] = useState({}); // { [orgId]: true }

  // Purge modal state
  const [purgeTarget, setPurgeTarget] = useState(null); // candidate row
  const [dryRunResult, setDryRunResult] = useState(null);
  const [dryRunLoading, setDryRunLoading] = useState(false);
  const [dryRunError, setDryRunError] = useState(null);
  const [confirmInput, setConfirmInput] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [purging, setPurging] = useState(false);
  const [purgeResult, setPurgeResult] = useState(null);
  const [purgeError, setPurgeError] = useState(null);

  // Message banner
  const [message, setMessage] = useState({ type: '', text: '' });

  const showMessage = (type, text) => setMessage({ type, text });

  const handleScan = async () => {
    setScanning(true);
    setMessage({ type: '', text: '' });
    try {
      const result = await scanStale();
      setCandidates(result.candidates || []);
      setHasScanned(true);
      if ((result.candidates || []).length === 0) {
        showMessage('info', 'No stale tenant candidates found.');
      }
    } catch (error) {
      showMessage('error', error.message || 'Failed to scan for stale tenants');
    } finally {
      setScanning(false);
    }
  };

  const setBusy = (orgId, busy) =>
    setRowBusy((prev) => ({ ...prev, [orgId]: busy }));

  const handleArchiveToggle = async (candidate) => {
    const isArchived = candidate.status === 'ARCHIVED';
    setBusy(candidate.org_id, true);
    try {
      const result = await archiveTenant(candidate.org_id, isArchived);
      setCandidates((prev) =>
        prev.map((c) =>
          c.org_id === candidate.org_id ? { ...c, status: result.new_status } : c
        )
      );
      showMessage(
        'success',
        `${candidate.org_id} ${isArchived ? 'unarchived' : 'archived'} (now ${result.new_status}).`
      );
    } catch (error) {
      showMessage('error', error.message || 'Archive action failed');
    } finally {
      setBusy(candidate.org_id, false);
    }
  };

  // ---- Purge modal ----------------------------------------------------------

  const openPurgeModal = async (candidate) => {
    setPurgeTarget(candidate);
    setDryRunResult(null);
    setDryRunError(null);
    setConfirmInput('');
    setAdminPassword('');
    setPurgeResult(null);
    setPurgeError(null);
    setDryRunLoading(true);

    // (a) FIRST call purge with dry_run=true to show the exact objects to drop.
    try {
      const result = await purgeTenant(candidate.org_id, { dryRun: true });
      setDryRunResult(result);
    } catch (error) {
      setDryRunError(error.message || 'Dry-run failed');
    } finally {
      setDryRunLoading(false);
    }
  };

  const closePurgeModal = () => {
    if (purging) return;
    setPurgeTarget(null);
    setDryRunResult(null);
    setDryRunError(null);
    setConfirmInput('');
    setAdminPassword('');
    setPurgeResult(null);
    setPurgeError(null);
  };

  // §8 gating: destructive button enabled only when the tenant is ARCHIVED,
  // the typed token exactly matches ORG_ID, AND a non-empty admin password is
  // entered (re-verified server-side on the real purge — AUTH HARDENING).
  const isArchived = purgeTarget?.status === 'ARCHIVED';
  const tokenMatches = !!purgeTarget && confirmInput === purgeTarget.org_id;
  const passwordEntered = adminPassword.trim().length > 0;
  const canPurge =
    isArchived &&
    tokenMatches &&
    passwordEntered &&
    !!dryRunResult &&
    !dryRunLoading &&
    !purging &&
    !purgeResult;

  const handleConfirmPurge = async () => {
    if (!canPurge || !purgeTarget) return;
    setPurging(true);
    setPurgeError(null);
    try {
      // (c) call purge with dry_run=false and confirm_token=<typed ORG_ID>.
      const result = await purgeTenant(purgeTarget.org_id, {
        dryRun: false,
        confirmToken: confirmInput,
        adminPassword,
      });
      setPurgeResult(result);
      // Remove the purged tenant from the candidate list.
      setCandidates((prev) => prev.filter((c) => c.org_id !== purgeTarget.org_id));
      showMessage(
        'success',
        `${purgeTarget.org_id} purged. Confirmation: ${result.confirmation_number || 'n/a'}`
      );
    } catch (error) {
      setPurgeError(error.message || 'Purge failed');
    } finally {
      setPurging(false);
    }
  };

  // ---- Rendering helpers ----------------------------------------------------

  const getStatusBadgeClasses = (status) => {
    switch (status) {
      case 'ACTIVE':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'SUSPENDED':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'ARCHIVED':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
  };

  const fmtDate = (val) => (val ? new Date(val).toLocaleString() : '-');
  const fmtBool = (val) =>
    val ? (
      <CheckCircleIcon className="h-5 w-5 text-green-500 inline" />
    ) : (
      <XMarkIcon className="h-5 w-5 text-gray-400 inline" />
    );

  const planned = dryRunResult?.planned_or_executed || {};

  return (
    <div className="space-y-6">
      {/* Message Banner */}
      {message.text && (
        <div
          className={`border-l-4 p-4 rounded-r-md ${
            message.type === 'error'
              ? 'bg-red-50 border-red-500 text-red-700 dark:bg-red-900/20'
              : message.type === 'success'
              ? 'bg-green-50 border-green-500 text-green-700 dark:bg-green-900/20'
              : 'bg-blue-50 border-blue-500 text-blue-700 dark:bg-blue-900/20'
          }`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              {message.type === 'error' && <ExclamationTriangleIcon className="h-5 w-5 mr-2" />}
              {message.type === 'success' && <CheckCircleIcon className="h-5 w-5 mr-2" />}
              <span>{message.text}</span>
            </div>
            <button onClick={() => setMessage({ type: '', text: '' })}>
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      {/* Header & Scan action */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
              <ServerStackIcon className="h-5 w-5 mr-2 text-[#FFC000]" />
              Stale Tenant Cleanup
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Scan for stale or test tenants, archive (reversible), then purge after a mandatory
              dry-run and exact-match confirmation.
            </p>
          </div>
          <button
            onClick={handleScan}
            disabled={scanning}
            className="px-4 py-2 bg-[#0E3056] hover:bg-[#1a4a7a] text-white rounded-md font-medium transition-colors disabled:opacity-50 flex items-center"
          >
            {scanning ? (
              <ArrowPathIcon className="h-5 w-5 mr-2 animate-spin" />
            ) : (
              <MagnifyingGlassIcon className="h-5 w-5 mr-2" />
            )}
            {scanning ? 'Scanning...' : 'Scan'}
          </button>
        </div>
      </div>

      {/* Candidates Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                {[
                  'Org ID',
                  'Org Name',
                  'Status',
                  'Last Upload',
                  'Uploads',
                  'Users',
                  'DB / Role',
                  'Signals',
                  'Actions',
                ].map((h) => (
                  <th
                    key={h}
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {scanning ? (
                <tr>
                  <td colSpan="9" className="px-4 py-8 text-center">
                    <ArrowPathIcon className="h-6 w-6 animate-spin mx-auto text-gray-400" />
                    <p className="mt-2 text-gray-500">Scanning tenants...</p>
                  </td>
                </tr>
              ) : !hasScanned ? (
                <tr>
                  <td colSpan="9" className="px-4 py-8 text-center text-gray-500">
                    Click <strong>Scan</strong> to find stale tenant candidates.
                  </td>
                </tr>
              ) : candidates.length === 0 ? (
                <tr>
                  <td colSpan="9" className="px-4 py-8 text-center text-gray-500">
                    No stale tenant candidates found.
                  </td>
                </tr>
              ) : (
                candidates.map((c) => {
                  const archived = c.status === 'ARCHIVED';
                  const busy = !!rowBusy[c.org_id];
                  return (
                    <tr key={c.org_id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-mono font-semibold text-gray-900 dark:text-white">
                        {c.org_id}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {c.org_name || '-'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeClasses(
                            c.status
                          )}`}
                        >
                          {c.status}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {fmtDate(c.last_upload_at)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {c.upload_count ?? '-'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {c.user_count ?? '-'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-center">
                        <span title="Database exists">{fmtBool(c.db_exists)}</span>
                        <span className="mx-1 text-gray-300">/</span>
                        <span title="Role exists">{fmtBool(c.role_exists)}</span>
                      </td>
                      <td className="px-4 py-4 text-sm">
                        <div className="flex flex-wrap gap-1 max-w-[14rem]">
                          {(c.stale_signals || []).length === 0 ? (
                            <span className="text-gray-400">-</span>
                          ) : (
                            c.stale_signals.map((sig) => (
                              <span
                                key={sig}
                                className="px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs"
                              >
                                {sig}
                              </span>
                            ))
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleArchiveToggle(c)}
                            disabled={busy}
                            className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-xs hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center disabled:opacity-50"
                          >
                            {archived ? (
                              <>
                                <ArchiveBoxXMarkIcon className="h-4 w-4 mr-1" />
                                Unarchive
                              </>
                            ) : (
                              <>
                                <ArchiveBoxIcon className="h-4 w-4 mr-1" />
                                Archive
                              </>
                            )}
                          </button>
                          <button
                            onClick={() => openPurgeModal(c)}
                            disabled={busy || !archived}
                            title={
                              archived
                                ? 'Purge tenant (requires dry-run + confirmation)'
                                : 'Tenant must be ARCHIVED before it can be purged'
                            }
                            className="px-3 py-1.5 bg-red-600 text-white rounded-md text-xs hover:bg-red-700 flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <TrashIcon className="h-4 w-4 mr-1" />
                            Purge
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Purge Modal */}
      {purgeTarget && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={closePurgeModal}
            />
            <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-auto z-10 text-left">
              {/* Header */}
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center">
                  <div className="flex items-center justify-center w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-full mr-3">
                    <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Purge Tenant: <span className="font-mono">{purgeTarget.org_id}</span>
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      This permanently drops the tenant's database, role, and registry row. This
                      action cannot be undone.
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-6 space-y-5 max-h-[60vh] overflow-y-auto">
                {/* Step 1: Dry-run preview */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                    Step 1 — Dry-run preview (objects that will be dropped)
                  </h4>
                  {dryRunLoading ? (
                    <div className="p-4 text-center">
                      <ArrowPathIcon className="h-6 w-6 animate-spin mx-auto text-gray-400" />
                      <p className="mt-2 text-sm text-gray-500">Running dry-run...</p>
                    </div>
                  ) : dryRunError ? (
                    <div className="p-4 rounded-md bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300 flex items-start">
                      <ExclamationTriangleIcon className="h-5 w-5 mr-2 flex-shrink-0" />
                      <span>{dryRunError}</span>
                    </div>
                  ) : dryRunResult ? (
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-md">
                          <p className="text-xs text-gray-500 dark:text-gray-400 uppercase">
                            Database
                          </p>
                          <p className="font-mono text-gray-900 dark:text-white">
                            {planned.db || '-'}
                          </p>
                        </div>
                        <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-md">
                          <p className="text-xs text-gray-500 dark:text-gray-400 uppercase">Role</p>
                          <p className="font-mono text-gray-900 dark:text-white">
                            {planned.role || '-'}
                          </p>
                        </div>
                        <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-md">
                          <p className="text-xs text-gray-500 dark:text-gray-400 uppercase">
                            Org Registry Row
                          </p>
                          <p className="font-mono text-gray-900 dark:text-white">
                            {planned.org_row || purgeTarget.org_id}
                          </p>
                        </div>
                        <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-md">
                          <p className="text-xs text-gray-500 dark:text-gray-400 uppercase">
                            User Rows Nulled
                          </p>
                          <p className="font-mono text-gray-900 dark:text-white">
                            {planned.user_rows_nulled ?? '-'}
                          </p>
                        </div>
                      </div>
                      {/* Guardrail result */}
                      <div
                        className={`p-3 rounded-md text-sm flex items-center ${
                          dryRunResult.guardrail === 'passed'
                            ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300'
                            : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
                        }`}
                      >
                        {dryRunResult.guardrail === 'passed' ? (
                          <ShieldCheckIcon className="h-5 w-5 mr-2" />
                        ) : (
                          <ShieldExclamationIcon className="h-5 w-5 mr-2" />
                        )}
                        Guardrail: {dryRunResult.guardrail || 'unknown'}
                      </div>
                    </div>
                  ) : null}
                </div>

                {/* Step 2: Confirm token */}
                {!purgeResult && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                      Step 2 — Confirm
                    </h4>
                    {!isArchived && (
                      <div className="mb-3 p-3 rounded-md bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 text-sm text-yellow-800 dark:text-yellow-300 flex items-start">
                        <ExclamationTriangleIcon className="h-5 w-5 mr-2 flex-shrink-0" />
                        <span>
                          This tenant is not ARCHIVED. It must be archived before it can be purged.
                        </span>
                      </div>
                    )}
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Type the exact ORG_ID{' '}
                      <span className="font-mono text-[#0E3056] dark:text-[#FFC000]">
                        {purgeTarget.org_id}
                      </span>{' '}
                      to enable purge:
                    </label>
                    <input
                      type="text"
                      aria-label="confirm-org-id"
                      value={confirmInput}
                      onChange={(e) => setConfirmInput(e.target.value)}
                      placeholder={purgeTarget.org_id}
                      disabled={purging}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white font-mono"
                    />
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mt-3 mb-1">
                      Re-enter the GDPR admin password (verified server-side for this
                      irreversible purge):
                    </label>
                    <input
                      type="password"
                      aria-label="admin-password"
                      value={adminPassword}
                      onChange={(e) => setAdminPassword(e.target.value)}
                      placeholder="Admin password"
                      autoComplete="off"
                      disabled={purging}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                )}

                {/* Purge error / result */}
                {purgeError && (
                  <div className="p-3 rounded-md bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300 flex items-start">
                    <ExclamationTriangleIcon className="h-5 w-5 mr-2 flex-shrink-0" />
                    <span>{purgeError}</span>
                  </div>
                )}
                {purgeResult && (
                  <div className="p-4 rounded-md bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-sm text-green-700 dark:text-green-300">
                    <div className="flex items-center mb-2">
                      <CheckCircleIcon className="h-5 w-5 mr-2" />
                      <span className="font-semibold">Tenant purged successfully.</span>
                    </div>
                    <p>
                      Confirmation number:{' '}
                      <span className="font-mono font-semibold">
                        {purgeResult.confirmation_number || 'n/a'}
                      </span>
                    </p>
                  </div>
                )}
              </div>

              {/* Footer actions */}
              <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={closePurgeModal}
                  disabled={purging}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                >
                  {purgeResult ? 'Close' : 'Cancel'}
                </button>
                {!purgeResult && (
                  <button
                    type="button"
                    onClick={handleConfirmPurge}
                    disabled={!canPurge}
                    className="px-4 py-2 bg-red-600 text-white rounded-md font-semibold hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {purging ? (
                      <>
                        <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                        Purging...
                      </>
                    ) : (
                      <>
                        <TrashIcon className="h-4 w-4 mr-2" />
                        Permanently Purge {purgeTarget.org_id}
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StaleTenantCleanupPanel;
